import { deviceCurrentRetrieve, productsRetrieve } from "@innoclouds/api";
import type { AxiosInstance, AxiosRequestConfig } from "axios";
import { storeToRefs } from "pinia";
import { SINGLE_MODIFIERS_GROUP_ID, SINGLE_MODIFIERS_GROUP_NAME } from "#/constants/modifiers";
import { toSelectedGroup } from "#/utils/customRenderModifiers";
import { useCardStore } from "./store/card";
import { useCartStore } from "./store/cart";
import { useHotmenuStore } from "./store/hotmenu";
import { useMainStore } from "./store/main";
import { useScheduleStore } from "./store/schedule";
import { useTableStore } from "./store/table";
import type { ICallStaffRequest, ICallStaffResponse } from "./types/iRequests";
import useAxios from "./utils/useAxiosToken";

interface ConnectionConfig {
	domain: string;
	area: string;
	pointId: string;
	orgId: string;
	user: string | null;
	currency_symbol: string;
	default_language: string;
	has_loyalty_default_user: string;
	loyalty: string;
	name: string;
	quick_add_simple: string;
	supported_languages: string;
	order_types?: Array<{
		id: string;
		name: string;
		image_link: string;
		type: string;
	}>;
}

class InitConnection {
	public static _config: ConnectionConfig | null = null;

	public static _axiosGet?: AxiosInstance;
	public static _axiosGetApiKey?: AxiosInstance;
	public static _axiosPostApiKey?: AxiosInstance;
	public static _axiosAuthPostApiKey?: AxiosInstance;

	public static _axiosPost?: AxiosInstance;
	public static _axiosAuthGet?: AxiosInstance;
	public static _axiosAuthPost?: AxiosInstance;

	public static _cardStore: any | null = null;
	public static _stateCard: any | null = null;
	public static _recountGroupTotalAmount: any | null = null;
	public static _isQrMenu = false;
	public constructor() {
		this.axiosInstanceGet = InitConnection._axiosGet;
		this.axiosInstancePost = InitConnection._axiosPost;

		this.axiosInstanceGetApiKey = InitConnection._axiosPostApiKey;
		this.axiosInstancePostApiKey = InitConnection._axiosPostApiKey;
		this.axiosInstanceAuthenticationPostApiKey = InitConnection._axiosAuthPostApiKey;

		this.axiosInstanceAuthenticationGet = InitConnection._axiosAuthGet;
		this.axiosInstanceAuthenticationPost = InitConnection._axiosAuthPost;
	}

	public axiosInstanceGet?: AxiosInstance;
	public axiosInstancePost?: AxiosInstance;
	public axiosInstanceAuthenticationGet?: AxiosInstance;
	public axiosInstanceAuthenticationPost?: AxiosInstance;
	public axiosInstanceGetApiKey?: AxiosInstance;
	public axiosInstancePostApiKey?: AxiosInstance;
	public axiosInstanceAuthenticationPostApiKey?: AxiosInstance;

	static create(config?: ConnectionConfig): InitConnection {
		if (config && !InitConnection._config) {
			InitConnection._initialize(config);
		}

		if (!InitConnection._config) {
			console.warn("InitConnection не инициализирован");
		}

		return new InitConnection();
	}

	private static _initialize(config: ConnectionConfig, auth: any = null) {
		const storeMain = useMainStore();
		const cardStore = useCardStore();

		const { config_lib } = storeToRefs(storeMain);
		const { stateCard, recountGroupTotalAmount } = storeToRefs(cardStore);

		InitConnection._config = config;

		InitConnection._cardStore = cardStore;
		InitConnection._stateCard = stateCard;
		InitConnection._recountGroupTotalAmount = recountGroupTotalAmount;

		config_lib.value = config;

		const axiosUtils = useAxios();

		InitConnection._axiosPostApiKey = axiosUtils.axiosInstanceGetApiKey;
		InitConnection._axiosPostApiKey = axiosUtils.axiosInstancePostApiKey;
		InitConnection._axiosAuthPostApiKey = axiosUtils.axiosInstanceAuthenticationPostApiKey;

		InitConnection._axiosGet = axiosUtils.axiosInstanceGet;
		InitConnection._axiosPost = axiosUtils.axiosInstancePost;
		InitConnection._axiosAuthGet = axiosUtils.axiosInstanceAuthenticationGet;
		InitConnection._axiosAuthPost = axiosUtils.axiosInstanceAuthenticationPost;
	}

	private currentUpsaleController: AbortController | null = null;

	protected get config() {
		return InitConnection._config;
	}
	protected get cardStore() {
		return InitConnection._cardStore;
	}
	protected get stateCard() {
		return InitConnection._stateCard;
	}
	protected get recountGroupTotalAmount() {
		return InitConnection._recountGroupTotalAmount;
	}

	async getGroupByID(groupId: string) {
		try {
			const response = await this.axiosInstanceGet?.get(`${this.config?.pointId}/group/${groupId}`);
			return response?.data;
		} catch (error) {
			console.error("Ошибка в request.ts[getGroupByID]", error);
			throw error;
		}
	}

	async getGroup() {
		try {
			const response = await this.axiosInstanceGet?.get(`${this.config?.pointId}/groups/`);
			return response?.data;
		} catch (e) {
			console.error("Error fetching products:", e);
			return [];
		}
	}

	async getProduct(id: string | null = null): Promise<any[] | null> {
		if (!id) return [];

		try {
			const response = await this.axiosInstanceGet?.get(`${this.config?.pointId}/products/${id}`);

			return response?.data;
		} catch (e) {
			console.error("Error fetching cardProducts:", e);
			return [];
		}
	}

	//Methods
	async getSingleProduct(id: string): Promise<any | void> {
		const storeCard = useCardStore();
		const hotmenu = useHotmenuStore();

		try {
			const isAnotherProduct = String(storeCard.stateCard.openCard?.id) !== String(id);
			if (isAnotherProduct) storeCard.isCombo = false;

			const orgUnitId = this.config?.pointId as string;

			const dataCache = hotmenu.products.get(id);
			if (dataCache) return this.fullCardProduct(dataCache);

			const data = await productsRetrieve(orgUnitId, id);
			return this.fullCardProduct(data);
		} catch (e) {
			console.warn("store card[getFetchProducts]:", e);
			throw e;
		}
	}

	fullCardProduct(data: any) {
		const storeCard = useCardStore();

		storeCard.stateCard.openCard = { ...data, amount: 1 };
		storeCard.stateCard.groupModifiers = (data.group_modifiers ?? []).map((group: any) => ({
			...group,
			group_modifiers: (group.group_modifiers ?? []).map((modifier: any) => ({ ...modifier })),
		}));
		if (data.modifiers?.length > 0) {
			storeCard.stateCard.groupModifiers.unshift({
				default_amount: 0,
				description: "",
				group_modifiers: data.modifiers.map((modifier: any) => ({ ...modifier })),
				id: SINGLE_MODIFIERS_GROUP_ID,
				image_link: null,
				max_amount: 1,
				min_amount: data.modifiers.reduce((sum: number, modifier: any) => (sum += modifier.min_amount), 0),
				name: SINGLE_MODIFIERS_GROUP_NAME,
				required: false,
			});
		}

		storeCard.stateCard.groupModifiersSelected = storeCard.stateCard.groupModifiers.map((group) => toSelectedGroup(group));
		storeCard.stateCard.productSizes = data.sizes;
		storeCard.defaultAmountModifiers();

		if (data.show_recommendations) {
			this.productUpsale(data.id);
		}
	}

	async callStaff(tableId: string, payload: ICallStaffRequest, options?: AxiosRequestConfig) {
		try {
			const response = await this.axiosInstancePost?.post<ICallStaffResponse>(
				`/org_unit/${this.config?.pointId}/table/${tableId}/call-staff/`,
				{ ...payload },
				options,
			);
			return response?.data?.response_text;
		} catch (error) {
			console.error("Ошибка[callStaff]", error);
			throw error;
		}
	}

	async confirmOrder(orderId: string): Promise<any> {
		const response = await this.axiosInstancePost?.post(`/org_unit/${this.config?.pointId}/confirm-order/`, { order: orderId });
		return response?.status === 200;
	}

	async createOrder(
		cartPayload: { items: any[]; combos: any[]; promo_code?: string },
		tableTitle: any,
		is_requestKiosk = true,
		comment: string,
		user: any,
		signal?: AbortSignal,
		locator?: string,
	): Promise<any> {
		const storeCart = useCartStore();
		const tableStore = useTableStore();

		const authorizedKiosk = user?.id ? this.axiosInstanceAuthenticationPostApiKey : this.axiosInstancePostApiKey;
		const requestUser = is_requestKiosk ? authorizedKiosk : this.axiosInstanceAuthenticationPost;

		const requestPath = is_requestKiosk ? "/device/order/create" : "/order/create";
		const orgId = is_requestKiosk ? this.config?.pointId : this.config?.pointId;

		const clearCartProducts = cartPayload.items || [];
		const clearCartCombos = cartPayload.combos || [];
		const promoCode = cartPayload.promo_code || undefined;
		let tabName = "";

		if (user?.first_name?.trim()) {
			tabName = user.first_name.trim();
		} else if (storeCart.tabName) {
			tabName = storeCart.tabName.trim();
		}

		const payload: Record<string, unknown> = {
			items: clearCartProducts,
			combos: clearCartCombos,
			table: tableTitle || null,
			comment,
			tab_name: tabName,
			locator: locator || undefined,
			promo_code: promoCode || undefined,
		};

		if (tableTitle) payload["table"] = tableTitle;

		if (!tableStore.isTableService && storeCart.selectedOrderType?.id) payload["order_type_id"] = storeCart.selectedOrderType?.id;

		const response = await requestUser?.post(`/org_unit/${orgId}${requestPath}`, payload, { signal });

		return response;
	}

	async getOrder(orderId: string): Promise<any> {
		const response = await this.axiosInstanceGet?.get(`order/${orderId}/`);
		return response?.data;
	}

	async extendOrder(clearCartProducts_lib: any): Promise<any> {
		const tableStore = useTableStore();
		if (!tableStore.id) throw Error(`table-service.notification.table-not-found`);
		if (!tableStore.selectedOrder) throw Error(`table-service.notification.order-not-found`);

		const requestUser = this.axiosInstanceAuthenticationPost;
		const payload: Record<string, unknown> = { items: clearCartProducts_lib, table: tableStore.id };

		const response = await requestUser?.post(`/org_unit/${this.config?.pointId}/order/${tableStore.selectedOrder.id}/extend`, payload);

		return response?.data;
	}

	async initPaymentLink(order: string, paymentMethod: string, extra: Record<string, unknown> = {}): Promise<any> {
		const requestUser = this.axiosInstanceAuthenticationPost;
		const storeMain = useMainStore();

		const payload: Record<string, unknown> = { order, provider_type: paymentMethod };
		if (storeMain.countBonus > 0) payload["by_bonuses"] = storeMain.countBonus;
		if (extra.amount || extra.amount === 0) payload["amount"] = extra.amount;
		if (extra.disable_back_btn) payload["disable_back_btn"] = extra.disable_back_btn;
		if (extra.items) payload["items"] = extra.items;
		if (payload["by_bonuses"] && payload["amount"]) payload["amount"] = +(+payload["amount"] - +payload["by_bonuses"]).toFixed(2);
		if (extra.email) payload["email"] = extra.email;
		if (extra.delivery_cost || extra.delivery_cost === 0) payload["delivery_cost"] = extra.delivery_cost;

		const response = await requestUser?.post("payment/init", payload);

		return response?.data;
	}

	async orderPay(id: string) {
		return this.axiosInstanceAuthenticationPostApiKey?.post(`order/${id}/pay/`, { order_id: id });
	}

	async sendOrderFeedback(orderId: string, payload: { score: number; comment: string }): Promise<{ user_friendly_message?: string } | undefined> {
		const response = await this.axiosInstanceAuthenticationPost?.post(`order/${orderId}/feedback/`, payload);
		return response?.data;
	}

	async getOrderDailyStats(dateFrom: string, dateTo: string): Promise<{ date: string; count: number }[]> {
		const orgId = this.config?.orgId;
		if (!orgId) return [];
		const params = new URLSearchParams({
			organization: orgId,
			date_from: dateFrom,
			date_to: dateTo,
		});
		const response = await this.axiosInstanceAuthenticationGet?.get(`order/daily-stats?${params.toString()}`);
		return response?.data?.results ?? [];
	}

	async getOrganization(orgId?: string): Promise<any> {
		const id = orgId ?? this.config?.orgId;
		const response = await this.axiosInstanceGet?.get(`org/organization/${id}`);
		return response?.data;
	}

	async getOrganizationSubscription(orgId?: string): Promise<any> {
		const id = orgId ?? this.config?.orgId;
		const orgUnitId = this.config?.pointId;
		const response = await this.axiosInstanceGet?.get(`org/organizations/${id}/subscription/`, {
			params: orgUnitId ? { org_unit: orgUnitId } : undefined,
		});
		return response?.data;
	}

	async getUserSubscription(): Promise<any> {
		const orgUnitId = this.config?.pointId;
		const response = await this.axiosInstanceAuthenticationGet?.get(`user/subscription`, {
			params: orgUnitId ? { org_unit: orgUnitId } : undefined,
		});
		return response?.data;
	}

	async purchaseSubscription(payload: { return_url: string; email: string }): Promise<any> {
		const orgUnitId = this.config?.pointId;
		const body = orgUnitId ? { ...payload, org_unit: orgUnitId } : payload;
		const response = await this.axiosInstanceAuthenticationPost?.post(`user/subscription/purchase`, body);
		return response?.data;
	}

	async calculateCombo(payload: any, signal?: AbortSignal) {
		// return await combosCalculateCreate(this.config?.pointId as string, payload, { signal });
		const response = await this.axiosInstancePostApiKey?.post(`/org_unit/${this.config?.pointId}/cart/calculate-discount/`, payload, { signal });

		if (response?.status === 200) {
			return response.data;
		}

		return response;
	}

	async getDeviceInfo() {
		const storeCart = useCartStore();
		const storeMain = useMainStore();
		const schedule = useScheduleStore();
		const response = await deviceCurrentRetrieve();

		if (response.order_types) storeCart.orderTypesDevice = response.order_types;
		if (response.payment_provider_type) storeCart.providerTypes = response.payment_provider_type;
		if (response.schedule) schedule.set(response.schedule);
		storeMain.markingGsSubstitute = response.marking_gs_substitute ?? "";

		return response;
	}

	async validateApiKey(apiKey: string, options?: AxiosRequestConfig) {
		const response = await this.axiosInstancePost?.post(`/device/org_unit/${this.config?.pointId}/validate-api-key/`, { api_key: apiKey }, options);
		return response?.status === 200;
	}

	async productUpsale(idProduct: string) {
		const cardStore = useCardStore();
		const { dataUpSale } = storeToRefs(cardStore);

		this.currentUpsaleController?.abort();

		const controller = new AbortController();
		this.currentUpsaleController = controller;

		try {
			const response = await this.axiosInstanceGet?.get(`/${this.config?.pointId}/products/${idProduct}/recommendation/`, { signal: controller.signal });

			if (!controller.signal.aborted) {
				dataUpSale.value = response?.data;
			}
		} catch (err: any) {
			if (err.name === "AbortError" || err.name === "CanceledError") {
				console.warn("Upsale отменён — пришёл новый запрос");
				return;
			}

			console.error("Ошибка upsale:", err);
			throw err;
		} finally {
			if (this.currentUpsaleController === controller) {
				this.currentUpsaleController = null;
			}
		}
	}
}

export { InitConnection };
